import React, { useState, useEffect } from 'react';
import SharedNavigation from './SharedNavigation';
import { schedulesService } from '../services/supabaseService';
import { Trash2, AlertTriangle, Search, Users, Calendar, Clock, CheckCircle, XCircle } from 'lucide-react';
import '../styles/DeleteSchedule.css';

const DeleteSchedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState(null);

  // Show notification function
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Fetch schedules from Supabase
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        setLoading(true);
        const data = await schedulesService.getAll();
        setSchedules(data);
      } catch (err) {
        console.error('Error fetching schedules:', err);
        setError('Failed to load schedules. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, []);

  // Filter schedules based on search
  const filteredSchedules = schedules.filter(schedule => {
    const searchLower = searchTerm.toLowerCase();
    const scheduleId = schedule.id.slice(0, 8);
    return scheduleId.toLowerCase().includes(searchLower) ||
           schedule.number_of_teams.toString().includes(searchLower) ||
           schedule.games_per_team.toString().includes(searchLower) ||
           schedule.weeks_to_complete.toString().includes(searchLower);
  });

  const handleSelectSchedule = (scheduleId) => {
    setSelectedSchedules(prev => 
      prev.includes(scheduleId) 
        ? prev.filter(id => id !== scheduleId)
        : [...prev, scheduleId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSchedules.length === filteredSchedules.length) {
      setSelectedSchedules([]);
    } else {
      setSelectedSchedules(filteredSchedules.map(s => s.id));
    }
  };

  const handleDeleteSingle = (schedule) => {
    setScheduleToDelete(schedule);
    setShowConfirmModal(true);
  };

  const handleDeleteSelected = () => {
    if (selectedSchedules.length === 0) return;
    setScheduleToDelete(null);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    
    try {
      if (scheduleToDelete) {
        // Delete single schedule
        await schedulesService.delete(scheduleToDelete.id);
        setSchedules(schedules.filter(s => s.id !== scheduleToDelete.id));
        showNotification('Schedule has been deleted successfully!', 'success');
      } else {
        // Delete selected schedules
        for (const scheduleId of selectedSchedules) {
          await schedulesService.delete(scheduleId);
        }
        setSchedules(schedules.filter(s => !selectedSchedules.includes(s.id)));
        showNotification(`${selectedSchedules.length} schedule(s) have been deleted successfully!`, 'success');
        setSelectedSchedules([]);
      }
      
      setShowConfirmModal(false);
      setScheduleToDelete(null);
    } catch (error) {
      console.error('Error deleting schedule(s):', error);
      showNotification('Failed to delete schedule(s). Please try again.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getTotalGames = (schedule) => {
    return schedule.schedule_data ? 
      schedule.schedule_data.reduce((total, week) => total + week.games.length, 0) : 0;
  };

  if (loading) {
    return (
      <div className="delete-schedule-page">
        <SharedNavigation />
        <div className="delete-schedule-content">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading schedules...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="delete-schedule-page">
      <SharedNavigation />
      
      {/* Toast Notification */}
      {notification && (
        <div className={`toast-notification ${notification.type}`}>
          {notification.type === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <XCircle size={20} />
          )}
          <span>{notification.message}</span>
        </div>
      )}

      <div className="delete-schedule-content">
        <div className="page-header">
          <h1>Delete Schedules</h1>
          <p>Manage and remove schedules from your system</p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message">
            <AlertTriangle size={20} />
            <p>{error}</p>
          </div>
        )}

        {/* Controls */}
        <div className="schedule-controls">
          <div className="search-filter-section">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search schedules by ID, teams, games, or weeks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="bulk-actions">
            <button
              className="select-all-btn"
              onClick={handleSelectAll}
              disabled={filteredSchedules.length === 0}
            >
              {selectedSchedules.length === filteredSchedules.length ? 'Deselect All' : 'Select All'}
            </button>
            
            {selectedSchedules.length > 0 && (
              <button
                className="delete-selected-btn"
                onClick={handleDeleteSelected}
              >
                <Trash2 size={16} />
                Delete Selected ({selectedSchedules.length})
              </button>
            )}
          </div>
        </div>

        {/* Schedules Table */}
        <div className="schedules-table-container">
          {filteredSchedules.length === 0 ? (
            <div className="no-schedules">
              <AlertTriangle size={48} />
              <h3>No schedules found</h3>
              <p>
                {searchTerm 
                  ? 'No schedules match your search criteria.' 
                  : 'You have no schedules to delete.'
                }
              </p>
            </div>
          ) : (
            <table className="schedules-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedSchedules.length === filteredSchedules.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Schedule ID</th>
                  <th>Teams</th>
                  <th>Games per Team</th>
                  <th>Weeks</th>
                  <th>Total Games</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSchedules.map((schedule) => (
                  <tr key={schedule.id} className={selectedSchedules.includes(schedule.id) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedSchedules.includes(schedule.id)}
                        onChange={() => handleSelectSchedule(schedule.id)}
                      />
                    </td>
                    <td className="schedule-id">#{schedule.id.slice(0, 8)}</td>
                    <td>
                      <div className="table-cell-content">
                        <Users size={16} />
                        <span>{schedule.number_of_teams}</span>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <Calendar size={16} />
                        <span>{schedule.games_per_team}</span>
                      </div>
                    </td>
                    <td>
                      <div className="table-cell-content">
                        <Clock size={16} />
                        <span>{schedule.weeks_to_complete}</span>
                      </div>
                    </td>
                    <td>{getTotalGames(schedule)}</td>
                    <td>{formatDate(schedule.created_at)}</td>
                    <td>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteSingle(schedule)}
                        title="Delete this schedule"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <div className="modal-header">
              <AlertTriangle size={24} className="warning-icon" />
              <h3>Confirm Deletion</h3>
            </div>
            
            <div className="modal-content">
              {scheduleToDelete ? (
                <div>
                  <p>
                    Are you sure you want to delete the schedule 
                    <strong> #{scheduleToDelete.id.slice(0, 8)}</strong>?
                  </p>
                  <div className="schedule-details-modal">
                    <p>• {scheduleToDelete.number_of_teams} teams</p>
                    <p>• {scheduleToDelete.games_per_team} games per team</p>
                    <p>• {scheduleToDelete.weeks_to_complete} weeks duration</p>
                    <p>• {getTotalGames(scheduleToDelete)} total games</p>
                  </div>
                </div>
              ) : (
                <p>
                  Are you sure you want to delete <strong>{selectedSchedules.length}</strong> selected schedule(s)?
                </p>
              )}
              <p className="warning-text">
                This action cannot be undone. All schedule data will be permanently removed.
              </p>
            </div>
            
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setShowConfirmModal(false);
                  setScheduleToDelete(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="confirm-delete-btn"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteSchedule;